import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer, FilterTabs, NotesField, StatusBadge } from '@/features/admin/ui';
import { useAuth } from '@/lib/auth/useAuth';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { adminQueries, auditLog, notify, type CreatorApplication } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/applications')({
  component: ApplicationsPage,
});

type Row = CreatorApplication & {
  profile: {
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
};

const FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
] as const;

function ApplicationsPage() {
  useRequireRole(['admin']); // admin-only; moderators 403 on direct nav (Spec 21)
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['value']>('pending');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<Row | null>(null);
  const [notes, setNotes] = useState('');

  const list = useQuery(adminQueries.applications(filter === 'all' ? null : filter));
  const rows = (list.data ?? []) as Row[];
  const filtered = search.trim()
    ? rows.filter((r) => {
        const s = search.toLowerCase();
        return (
          r.full_name.toLowerCase().includes(s) ||
          r.profile?.display_name?.toLowerCase().includes(s) ||
          r.profile?.username?.toLowerCase().includes(s)
        );
      })
    : rows;

  const decide = useMutation({
    mutationFn: async ({ row, action }: { row: Row; action: 'approved' | 'rejected' }) => {
      if (action === 'rejected' && !notes.trim()) {
        throw new Error('Reviewer notes are required to reject an application.');
      }
      const { error } = await supabase
        .from('creator_applications')
        .update({
          status: action,
          reviewer_id: user?.id,
          reviewer_notes: notes.trim() || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', row.id);
      if (error) throw error;

      // The apply_creator_decision DB trigger propagates role/creator_status.
      await auditLog(
        action === 'approved' ? 'application.approved' : 'application.rejected',
        'creator_applications',
        row.id,
        { applicant: row.user_id },
      );
      await notify(
        action === 'approved' ? 'creator_application_approved' : 'creator_application_rejected',
        null,
        action === 'approved'
          ? {
              name: row.profile?.display_name ?? row.full_name,
              creatorUrl: `${window.location.origin}/creator`,
            }
          : { name: row.profile?.display_name ?? row.full_name, reviewerNotes: notes.trim() },
      );
    },
    onSuccess: (_d, v) => {
      toast.success(v.action === 'approved' ? 'Application approved.' : 'Application rejected.');
      setActive(null);
      setNotes('');
      void qc.invalidateQueries({ queryKey: ['admin', 'applications'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Action failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Review" title="Contributor Applications" />
      <input
        type="search"
        placeholder="Search by name or username"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mt-6 w-full max-w-sm rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
      <div className="mt-4">
        <FilterTabs options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">No applications.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {filtered.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => {
                  setActive(r);
                  setNotes(r.reviewer_notes ?? '');
                }}
                className="block w-full rounded-md border border-ink/10 bg-bg-light/40 p-4 text-left hover:border-oxblood/40"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-serif text-base text-ink">
                    {r.profile?.display_name ?? r.full_name}
                    {r.profile?.username && (
                      <span className="ml-2 font-sans text-xs text-ink/50">
                        @{r.profile.username}
                      </span>
                    )}
                  </p>
                  <StatusBadge status={r.status} />
                </div>
                <p className="mt-1 font-sans text-xs text-ink/50">
                  Applied {new Date(r.created_at).toLocaleDateString()} · {r.intended_focus}
                </p>
                <p className="mt-2 line-clamp-2 font-sans text-sm text-ink/70">{r.background}</p>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.profile?.display_name ?? active?.full_name ?? 'Application'}
      >
        {active && (
          <div className="space-y-5">
            <Field label="Full name">{active.full_name}</Field>
            <Field label="Intended focus">{active.intended_focus}</Field>
            <Field label="Background">
              <span className="whitespace-pre-wrap">{active.background}</span>
            </Field>
            <Field label="Sample work">
              <ul className="space-y-1">
                {active.sample_work_urls.map((u) => (
                  <li key={u}>
                    <a
                      href={u}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-oxblood underline"
                    >
                      {u}
                    </a>
                  </li>
                ))}
              </ul>
            </Field>

            {active.status === 'pending' ? (
              <div className="space-y-4 border-t border-ink/10 pt-5">
                <NotesField value={notes} onChange={setNotes} />
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ row: active, action: 'approved' })}
                    className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ row: active, action: 'rejected' })}
                    className="rounded-md border border-oxblood px-4 py-2 font-sans text-sm font-medium text-oxblood hover:bg-oxblood/10 disabled:opacity-60"
                  >
                    Reject
                  </button>
                </div>
                <p className="font-sans text-xs text-ink/50">
                  Rejecting requires notes — they are sent to the applicant verbatim.
                </p>
              </div>
            ) : (
              <div className="border-t border-ink/10 pt-5">
                <Field label="Decision">
                  <StatusBadge status={active.status} />
                </Field>
                {active.reviewer_notes && (
                  <Field label="Reviewer notes">
                    <span className="whitespace-pre-wrap">{active.reviewer_notes}</span>
                  </Field>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/50">{label}</p>
      <div className="font-sans text-sm text-ink/80">{children}</div>
    </div>
  );
}
